import format from "date-fns/format"
import parseISO from "date-fns/parseISO"
import PropTypes from "prop-types"
import React, { useContext } from "react"
import { Controller } from "react-hook-form"

import Form from "../../../../components/Form"
import { SignContractWizardContext } from "../SignContractWizardContext"

const RequiredFormInput = ({
  inputKey,
  label,
  value,
  form,
  type,
  message,
  warning,
  minDate,
}) => {
  const { dispatch } = useContext(SignContractWizardContext)

  const {
    register,
    control,
    formState: { errors },
  } = form

  const handleTextChange = (eventHandler) => (event) => {
    eventHandler(event)

    const payload = recursivelySet(inputKey, event.target.value)

    dispatch({
      type: "REQUIRED_INFORMATION_CHANGED",
      payload,
    })
  }

  const handleDateChange = (eventHandler) => (date) => {
    const dateString = date ? format(date, "yyyy-MM-dd") : null

    eventHandler(wrapInEvent(dateString))

    const payload = recursivelySet(inputKey, dateString)

    dispatch({
      type: "REQUIRED_INFORMATION_CHANGED",
      payload,
    })
  }

  const wrapInEvent = (value) => {
    return { target: { value } }
  }

  const errorsForKey = recursivelyGet(inputKey, errors)

  const componentForType = () => {
    switch (type) {
      case "date": {
        const effectiveMinDate = minDate ?? new Date()
        const yearRangeStart = effectiveMinDate.getFullYear()
        const maxYear = yearRangeStart + 10

        return (
          <Controller
            name={inputKey}
            control={control}
            defaultValue={value}
            rules={{
              required: `${label} is required.`,
            }}
            render={({ field: { onChange, onBlur, name } }) => {
              return (
                <Form.DatePicker
                  id={inputKey}
                  minDate={effectiveMinDate}
                  value={value ? parseISO(value) : value}
                  onChange={handleDateChange((date) => {
                    onChange(date)
                    onBlur(date)
                  })}
                  onBlur={onBlur}
                  name={name}
                  hasErrors={Boolean(errorsForKey)}
                  renderCustomHeader={(props) => (
                    <Form.DatePicker.QuickNavHeader
                      {...props}
                      yearRangeStart={yearRangeStart}
                      yearRangeEnd={maxYear}
                    />
                  )}
                  maxDate={new Date(maxYear, 11, 31)}
                />
              )
            }}
          />
        )
      }
      default: {
        const options = {
          value,
          required: `${label} is required.`,
        }

        const { onChange, onBlur, name, ref } = register(inputKey, options)

        return (
          <Form.TextField
            id={inputKey}
            onChange={handleTextChange(onChange)}
            onBlur={onBlur}
            name={name}
            ref={ref}
            hasErrors={Boolean(errorsForKey)}
            type={type}
          />
        )
      }
    }
  }

  return (
    <div>
      <Form.Label htmlFor={inputKey} required>
        {label}
      </Form.Label>
      <div className="flex flex-col space-y-2">
        {componentForType()}
        <div className="font-semibold">
          {message ? (
            <span className="text-red-600">{message}</span>
          ) : errorsForKey?.message ? (
            <span className="text-red-600">{errorsForKey.message}</span>
          ) : warning ? (
            <span className="text-yellow-700">{warning}</span>
          ) : (
            <span>&nbsp;</span>
          )}
        </div>
      </div>
    </div>
  )
}

RequiredFormInput.propTypes = {
  inputKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  form: PropTypes.shape({
    register: PropTypes.func.isRequired,
    control: PropTypes.object.isRequired,
    formState: PropTypes.shape({
      errors: PropTypes.object.isRequired,
    }).isRequired,
  }).isRequired,
  type: PropTypes.oneOf(["text", "date", "number"]),
  message: PropTypes.string,
  warning: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
}

export const recursivelySet = (key, value) => {
  const keys = key.split(".")
  if (keys.length === 1) return { [keys[0]]: value }
  const [first, ...rest] = keys
  return { [first]: recursivelySet(rest.join("."), value) }
}

export const recursivelyGet = (key, obj) => {
  const keys = key.split(".")
  if (obj === undefined) return undefined
  if (keys.length === 1) return obj[keys[0]]
  const [first, ...rest] = keys
  return recursivelyGet(rest.join("."), obj[first])
}

export default RequiredFormInput
